import {
  type HttpInterceptorFn,
  type HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { delay, of } from 'rxjs';

import { APP_ENV } from '../tokens/app-environment.token';
import { getAssetsMockTiming } from './assets-api-mock.timing';
import {
  mockFindCrmOpportunityLink,
  mockListCrmOpportunitiesByAccount,
} from './crm-opportunity-mock.fixtures';
import { logMockApiExchange } from './mock-api-logger';

import type { MockApiFlags } from '../../../environments/environment.model';
import type {
  KnowledgeItemListResponse,
  KnowledgeItemResponse,
} from '../../features/knowledge/models/knowledge.models';
import type { MomentumUser } from '../../shared/models/momentum-user.models';
import type { IndustryDto } from '../../shared/industries/models/industry.models';
import type { OpportunityListResponseDto } from '../../features/opportunities/models/opportunity-list.models';
import type { OpportunityResponseDto } from '../../features/opportunities/opportunity.mapper';
import {
  mockCatalogUserFromEmail,
  mockMomentumUserFromOId,
  mockUserInformation,
  searchMockDirectoryUsers,
} from './users-api-mock.fixtures';
import {
  furthestApiStep,
  type OpportunityApiStep,
} from '../../features/opportunities/opportunity.mapper';
import {
  mockHandleOpportunityAssessmentGet,
  mockHandleOpportunityAssessmentPatch,
  mockHandleOpportunityBidRisksGet,
  mockHandleOpportunityIntelligenceAnalyze,
  mockHandleOpportunityIntelligenceFinalize,
  mockHandleOpportunityIntelligenceGet,
  mockHandleOpportunityValueAnalyze,
  mockHandleOpportunityValueGet,
  mockHandleBidRiskAnalyze,
} from './opportunity-assessment-api-mock.handlers';
import { mockHandleAssetsRoute, type MockAssetsBinaryResponse } from './assets-api-mock.handlers';
import {
  mockHandleIndustriesRoute,
  MOCK_INDUSTRY_ALL,
  MOCK_INDUSTRY_ENERGY,
  MOCK_INDUSTRY_FSI_BANKING,
  MOCK_INDUSTRY_FSI_INSURANCE,
  MOCK_INDUSTRY_HEALTH,
  MOCK_INDUSTRY_OTHER,
  MOCK_INDUSTRY_RETAIL,
  MOCK_INDUSTRY_TRANSPORT,
} from './industries-api-mock.handlers';
import {
  mockHandleScotsmanAcknowledgeAiSuggestionChange,
  mockHandleScotsmanAssessmentAnalyze,
  mockHandleScotsmanAssessmentFinalize,
  mockHandleScotsmanAssessmentGet,
  mockHandleScotsmanDimensionPatch,
} from './scotsman-api-mock.handlers';
import {
  mockApplyQualificationMaterialChange,
  mockPatchHasQualificationMetadataChange,
  mockQualificationBindOpportunities,
} from './qualification-mock.helpers';
import {
  mockHandleDiscoveryIntelligenceAnalyze,
  mockHandleDiscoveryIntelligenceFinalize,
  mockHandleDiscoveryIntelligenceGet,
} from './discovery-intelligence-mock.handlers';
import { mockHandleAccountsRoute } from './accounts-api-mock.handlers';
import { mockHandleWorkspaceFilesRoute } from './workspace-files-mock.handlers';
import { mockHandleCompaniesRoute, MOCK_COMPANY_ALL } from './companies-api-mock.handlers';
import {
  mockHandleQualificationUploadFinalize,
  mockHandleQualificationWizardStateGet,
} from './qualification-wizard-mock.handlers';
import {
  mockHandleSmartDiscoveryAnswerModelsGet,
  mockHandleWorkspaceConversationsChatPost,
  mockHandleWorkspaceConversationsCreatePost,
  mockHandleWorkspaceConversationsDetailGet,
  mockHandleWorkspaceConversationsListGet,
  mockHandleWorkspaceConversationsMessagesGet,
} from './workspace-conversations-mock.handlers';

/**
 * Mock API interceptor — active until the .NET microservice backend is available.
 *
 * Intercepts all requests to `/api/*` and returns stub data.
 * Non-API requests are passed through unchanged.
 *
 * HOW TO DISABLE PER FEATURE:
 * Set the relevant flag to `false` in the environment file, e.g.:
 *   enableMockApi: { proposals: true, knowledge: true, smartDiscovery: true, scotsman: true }
 * All flags are `false` in `environment.prod.ts`, and the interceptor is never
 * registered when every flag is false (see app.config.ts).
 *
 * HOW TO ADD STUBS:
 * Add an entry to MOCK_RESPONSES keyed by `METHOD /api/path`.
 * The value is the response body that will be returned with HTTP 200.
 * For routes with path parameters (e.g. `/api/knowledge-items/:id`), add a
 * handler to the PATTERN_HANDLERS array below.
 */

const SIMULATED_DELAY_MS = 400;

type MockDb = Record<string, unknown>;

/** Stubs server-computed needsReview for offline mock (UTC calendar date, same idea as API). */
function mockStubNeedReview(reviewDate: string | null | undefined): boolean {
  if (reviewDate == null || !String(reviewDate).trim()) return false;
  const day = String(reviewDate).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  return day <= new Date().toISOString().slice(0, 10);
}

const MOCK_MOMENTUM_USER: MomentumUser = {
  oId: '11111111-2222-3333-4444-555555555555',
  name: 'Jane Doe',
  email: 'jane@zuehlke.com',
};

const MOCK_KNOWLEDGE_DEFAULT_APPLICABILITY = {
  phases: ['ALL'],
  sections: [],
  industries: [MOCK_INDUSTRY_ALL],
  companies: [MOCK_COMPANY_ALL],
  procurementRoutes: ['ALL'],
};

const MOCK_KNOWLEDGE_ITEMS: KnowledgeItemResponse[] = (
  [
    {
      id: 'ki-002',
      type: 'Guideline',
      guidelineCategory: 'DataAndCompliance',
      title: 'No secret project data in Momentum PoC',
      owner: mockCatalogUserFromEmail('mock.personf@zuehlke.com'),
      status: 'Verified',
      reviewDate: '2025-01-15',
      version: 1,
      enforcementLevel: 'Mandatory',
      guideline: {
        instruction:
          'Under no circumstances should confidential project data, client names (beyond what is publicly known), or internal financial figures be entered into Momentum during the PoC phase.',
        rationale:
          'Protects client and company confidentiality during early AI tooling evaluation.',
      },
      createdAt: '2026-01-10T08:00:00Z',
      updatedAt: '2026-03-01T11:00:00Z',
    },
    {
      id: 'ki-003',
      type: 'Advice',
      adviceCategory: 'ClientResearch',
      title: 'Check LinkedIn for buying group roles before a bid',
      owner: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
      status: 'Unverified',
      reviewDate: '2026-12-01',
      version: 1,
      enforcementLevel: 'Recommended',
      advice: {
        recommendation:
          'Before submitting a bid, use LinkedIn Sales Navigator to identify all buying group members (economic buyer, champion, technical buyer, legal). Map their priorities to proposal sections.',
      },
      createdAt: '2026-01-20T09:00:00Z',
      updatedAt: '2026-02-28T16:00:00Z',
    },
    {
      id: 'ki-004',
      type: 'Fact',
      factCategory: 'KeyFiguresAndStats',
      title: 'Zühlke headcount as of Q4 2024',
      owner: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
      status: 'Verified',
      reviewDate: '2026-12-31',
      version: 3,
      enforcementLevel: 'Informational',
      fact: {
        detail:
          'As of Q4 2024, Zühlke employs approximately 1,850 people across 14 offices in Switzerland, Germany, UK, Austria, Singapore, and Serbia.',
      },
      createdAt: '2025-11-01T12:00:00Z',
      updatedAt: '2026-02-14T10:00:00Z',
    },
    {
      id: 'ki-005',
      type: 'Guideline',
      guidelineCategory: 'PricingAndCommercial',
      title: 'Day rate approval requirement',
      owner: mockCatalogUserFromEmail('mock.personc@zuehlke.com'),
      status: 'Verified',
      version: 2,
      enforcementLevel: 'Mandatory',
      guideline: {
        instruction:
          'Any proposal containing day rates above CHF 2,500 must be reviewed and signed off by a Vice President or above before submission to the client. This applies to all geographies.',
        rationale: 'Ensures commercial governance and pricing consistency across all geographies.',
      },
      createdAt: '2025-12-01T08:00:00Z',
      updatedAt: '2026-01-20T09:00:00Z',
    },
    {
      id: 'ki-007',
      type: 'Advice',
      adviceCategory: 'BidWriting',
      title: 'Lead with client pain point in executive summary',
      owner: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
      status: 'Verified',
      reviewDate: '2026-12-01',
      version: 2,
      enforcementLevel: 'Recommended',
      advice: {
        recommendation:
          "Always open the executive summary by naming the client's core business challenge before introducing Zühlke's solution. The first paragraph should make the client feel understood, not impressed by Zühlke.",
      },
      createdAt: '2026-03-05T09:00:00Z',
      updatedAt: '2026-03-08T11:00:00Z',
    },
    {
      id: 'ki-008',
      type: 'Advice',
      adviceCategory: 'BidWriting',
      title: 'Avoid generic capability lists in the solution section',
      owner: mockCatalogUserFromEmail('mock.personb@zuehlke.com'),
      status: 'Verified',
      reviewDate: '2027-03-01',
      version: 1,
      enforcementLevel: 'Recommended',
      advice: {
        recommendation:
          'Do not include a generic list of Zühlke services or capabilities in the solution section. Every capability mentioned must be directly linked to a specific client need or outcome stated in the brief.',
      },
      createdAt: '2026-02-20T11:00:00Z',
      updatedAt: '2026-02-20T11:00:00Z',
    },
    {
      id: 'ki-009',
      type: 'Advice',
      adviceCategory: 'Commercials',
      title: 'Quantify delivery risk reduction in commercial section',
      owner: mockCatalogUserFromEmail('mock.personc@zuehlke.com'),
      status: 'Unverified',
      reviewDate: '2026-09-01',
      version: 1,
      enforcementLevel: 'Recommended',
      advice: {
        recommendation:
          'When presenting commercial terms, always include a short "Risk & Mitigation" sub-section that maps each key delivery risk to a concrete mitigation measure. Link pricing premiums explicitly to risk reduction activities.',
      },
      createdAt: '2026-03-01T14:00:00Z',
      updatedAt: '2026-03-01T14:00:00Z',
    },
    {
      id: 'ki-010',
      type: 'Fact',
      factCategory: 'About',
      title: 'Zühlke founding and ownership',
      owner: mockCatalogUserFromEmail('marketing@zuehlke.com'),
      status: 'Verified',
      version: 1,
      enforcementLevel: 'Informational',
      fact: {
        detail:
          'Zühlke was founded in 1968 and is employee-owned. It has no external shareholders, which enables long-term thinking over short-term profit maximisation.',
      },
      createdAt: '2025-10-01T10:00:00Z',
      updatedAt: '2026-01-05T11:00:00Z',
    },
    {
      id: 'ki-011',
      type: 'Guideline',
      guidelineCategory: 'BrandAndTone',
      title: 'Use "we" not "Zühlke" in proposal body text',
      owner: mockCatalogUserFromEmail('marketing@zuehlke.com'),
      status: 'Verified',
      version: 1,
      enforcementLevel: 'Recommended',
      guideline: {
        instruction:
          'In proposal body text, prefer first-person plural ("we", "our team") over third-person references to "Zühlke". This creates a more direct and engaging tone.',
        rationale: 'Improves readability and aligns with Zühlke brand tone guidelines.',
      },
      createdAt: '2026-02-01T09:00:00Z',
      updatedAt: '2026-02-01T09:00:00Z',
    },
  ] as Omit<KnowledgeItemResponse, 'needsReview'>[]
).map((row) => ({
  ...row,
  applicability: row.applicability ?? MOCK_KNOWLEDGE_DEFAULT_APPLICABILITY,
  needsReview: mockStubNeedReview(row.reviewDate ?? undefined),
}));

const MOCK_KNOWLEDGE_STATS_RESPONSE = {
  totalItems: 9,
  facts: 2,
  guidelines: 3,
  advices: 4,
  verified: 7,
  unverified: 2,
  needReview: 1,
  recentlyAdded: 4,
  recentlyEdited: 3,
};

const MOCK_OPPORTUNITIES_STATS_RESPONSE = {
  totalOpportunities: 14,
  qualifiedOpportunities: 6,
  newInLast7Days: 2,
  pipelineValueChf: 18_400_000,
  qualificationRatePercent: 46,
};

function readUrlSearchParams(url: string): URLSearchParams {
  const q = url.indexOf('?');
  return q < 0 ? new URLSearchParams() : new URLSearchParams(url.slice(q + 1));
}

function mockKnowledgeItemMatchesGovernanceFilters(
  item: KnowledgeItemResponse,
  governanceFilters: string[],
): boolean {
  if (governanceFilters.length === 0) return true;
  return governanceFilters.some((f) => {
    if (f === 'Verified') return item.status === 'Verified';
    if (f === 'Unverified') return item.status === 'Unverified';
    if (f === 'NeedReview') return item.needsReview;
    return false;
  });
}

function mockKnowledgeListBody(url: string): KnowledgeItemListResponse {
  const params = readUrlSearchParams(url);
  const governanceFilters = params.getAll('governanceFilter');
  let items = MOCK_KNOWLEDGE_ITEMS.filter((item) =>
    mockKnowledgeItemMatchesGovernanceFilters(item, governanceFilters),
  );

  const apiType = params.get('type');
  if (apiType) {
    items = items.filter((i) => i.type === apiType);
  }

  const ownerEmail = params.get('ownerEmail')?.trim();
  if (ownerEmail) {
    items = items.filter((i) => (i.owner?.email ?? '').toLowerCase() === ownerEmail.toLowerCase());
  }

  const factCategory = params.get('factCategory');
  if (factCategory) {
    items = items.filter((i) => i.type === 'Fact' && i.factCategory === factCategory);
  }
  const guidelineCategory = params.get('guidelineCategory');
  if (guidelineCategory) {
    items = items.filter(
      (i) => i.type === 'Guideline' && i.guidelineCategory === guidelineCategory,
    );
  }
  const adviceCategory = params.get('adviceCategory');
  if (adviceCategory) {
    items = items.filter((i) => i.type === 'Advice' && i.adviceCategory === adviceCategory);
  }

  const includeTotal = params.get('includeTotalCount') === 'true';
  return {
    items,
    continuationToken: null,
    totalCount: includeTotal ? items.length : undefined,
  };
}

function mockOpportunityListBody(url: string): OpportunityListResponseDto {
  const params = readUrlSearchParams(url);
  const pageSize = Math.max(1, Number.parseInt(params.get('pageSize') ?? '50', 10) || 50);
  const continuationToken = params.get('continuationToken');
  const offset = continuationToken ? Number.parseInt(continuationToken, 10) || 0 : 0;

  const sorted = [...MOCK_OPPORTUNITIES].sort((left, right) => {
    const leftCreated = Date.parse(left.createdAt ?? '') || 0;
    const rightCreated = Date.parse(right.createdAt ?? '') || 0;
    if (leftCreated !== rightCreated) {
      return rightCreated - leftCreated;
    }
    return (right.id ?? '').localeCompare(left.id ?? '');
  });

  const pageItems = sorted.slice(offset, offset + pageSize);
  const nextOffset = offset + pageItems.length;
  const hasMore = nextOffset < sorted.length;

  return {
    items: pageItems,
    continuationToken: hasMore ? String(nextOffset) : null,
  };
}

function mockKnowledgeVersionListBody(itemId: string): { versions: unknown[] } {
  const item = MOCK_KNOWLEDGE_ITEMS.find((i) => i.id === itemId);
  if (!item) {
    return { versions: [] };
  }

  const maxV = item.version ?? 1;
  const versions: unknown[] = [];
  for (let i = 1; i <= maxV; i++) {
    versions.push({
      id: `v-${i}`,
      knowledgeItemId: itemId,
      version: i,
      changeType: i === 1 ? 0 : 1,
      changedAt: i === maxV ? item.updatedAt : item.createdAt,
      changedBy: MOCK_MOMENTUM_USER,
      changeReason: null,
      previousVersion: i > 1 ? i - 1 : null,
    });
  }
  return { versions };
}

function mockKnowledgeVersionDetailBody(
  itemId: string,
  version: number,
): Record<string, unknown> | undefined {
  const item = MOCK_KNOWLEDGE_ITEMS.find((i) => i.id === itemId);
  if (!item) {
    return undefined;
  }

  const maxV = item.version ?? 1;
  if (version < 1 || version > maxV || Number.isNaN(version)) {
    return undefined;
  }

  let snapshot: KnowledgeItemResponse = { ...item, version };
  if (itemId === 'ki-007' && version === 1 && maxV >= 2) {
    snapshot = {
      ...item,
      version: 1,
      advice: item.advice
        ? {
            ...item.advice,
            recommendation: `${item.advice.recommendation} (draft)`,
          }
        : item.advice,
      updatedAt: item.createdAt,
    };
  }

  return {
    id: `v-${version}`,
    knowledgeItemId: itemId,
    version,
    changeType: version === 1 ? 0 : 1,
    changedAt: version === maxV ? item.updatedAt : item.createdAt,
    changedBy: MOCK_MOMENTUM_USER,
    changeReason: null,
    previousVersion: version > 1 ? version - 1 : null,
    snapshot,
  };
}

function mockWorkspaceId(n: number): string {
  return `00000000-0000-0000-0000-${String(n).padStart(12, '0')}`;
}

const MOCK_OPPORTUNITY_COMPANY_BY_LEGACY: Record<string, { id: string; name: string }> = {
  CH: { id: '20000000-0000-4000-8000-000000000002', name: 'ZCH' },
  DE: { id: '20000000-0000-4000-8000-000000000006', name: 'ZDE' },
  AT: { id: '20000000-0000-4000-8000-000000000005', name: 'ZAT' },
  UK: { id: '20000000-0000-4000-8000-000000000003', name: 'ZUK' },
};

type MockOpportunitySeed = Omit<OpportunityResponseDto, 'id' | 'workspaceId' | 'step'> & {
  /** Legacy mock seed — converted to nested `account` in `mockOpp`. */
  company?: string;
  industry?: IndustryDto;
};

const MOCK_CRM_ORGANIZATION_URL = 'https://zuhlke-uat.crm4.dynamics.com';

function mockCrmOpportunityWebUrl(crmOpportunityId?: string | null): string | undefined {
  const id = crmOpportunityId?.trim();
  if (!id) {
    return undefined;
  }
  return `${MOCK_CRM_ORGANIZATION_URL}/main.aspx?pagetype=entityrecord&etn=opportunity&id=${id}`;
}

function mockOpp(
  idSuffix: string,
  n: number,
  seed: MockOpportunitySeed,
  step: OpportunityApiStep = 'UploadAndStart',
): OpportunityResponseDto {
  const { company: legacyCompany, industry: legacyIndustry, account, ...rest } = seed;
  const resolvedAccount =
    account ??
    (legacyIndustry && legacyCompany
      ? {
          id: `acc-mock-${idSuffix}`,
          name: rest.customerName,
          industry: legacyIndustry,
          company:
            MOCK_OPPORTUNITY_COMPANY_BY_LEGACY[legacyCompany.trim().toUpperCase()] ??
            MOCK_OPPORTUNITY_COMPANY_BY_LEGACY['CH'],
        }
      : undefined);

  const row: OpportunityResponseDto = {
    id: `pr-${idSuffix}`,
    workspaceId: mockWorkspaceId(n),
    step,
    ...rest,
    account: resolvedAccount,
  };
  if (!row.sharePointFolderName && row.customerName && row.title) {
    row.sharePointFolderName = `${row.customerName} - ${row.title}`;
  }
  row.crmOpportunityWebUrl =
    row.crmOpportunityWebUrl ?? mockCrmOpportunityWebUrl(row.crmOpportunityId);
  return row;
}

const MOCK_OPPORTUNITIES: OpportunityResponseDto[] = [
  mockOpp(
    '001',
    1,
    {
      title: 'Digital Platform Modernisation',
      customerName: 'Example Rail Corp',
      cNumber: 'C-34153',
      dealSize: 'L',
      company: 'CH',
      procurementRoute: 'ByInvitation',
      industry: MOCK_INDUSTRY_TRANSPORT,
      submissionDeadline: '2025-04-14T12:00:00.000Z',
      opportunityOwner: mockCatalogUserFromEmail('mock.personb@zuehlke.com'),
      proposalManager: { email: 'mock.persona@zuehlke.com' },
      briefDescription:
        "Zühlke is competing to modernise the client's core digital passenger platform, covering ticketing, journey planning, and real-time data services. We hold a strong reference advantage from prior rail projects and have already aligned on the technical architecture in pre-sales discussions.",
      salesStage: 'Qualify',
      miroWebUrl: 'https://miro.com/app/board/example-rail-modernisation',
      sharePointFolderWebUrl:
        'https://zuhlke.sharepoint.com/corporate/Sales%20&%20Delivery%20Excellence/ProposalsDev/Digital%20Platform%20Modernisation',
      isFrameContract: false,
      currency: 'CHF',
      tcvLocal: 2_400_000,
      tcv: 2_400_000,
      bidBudget: 120_000,
      accountPotential: 'Medium',
      existingRelationship: 'Partial',
      createdAt: '2025-01-12T08:00:00Z',
      updatedAt: '2025-02-18T10:00:00Z',
    },
    'OpportunityMetadata',
  ),
  mockOpp(
    '002',
    2,
    {
      title: 'Claims Automation Suite',
      customerName: 'Northwind Insurance AG',
      cNumber: 'C-34289',
      dealSize: 'M',
      company: 'CH',
      industry: MOCK_INDUSTRY_FSI_INSURANCE,
      submissionDeadline: '2025-04-22T12:00:00.000Z',
      opportunityOwner: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
      proposalManager: mockCatalogUserFromEmail('mock.personc@zuehlke.com'),
      briefDescription:
        "This engagement targets end-to-end automation of the insurer's motor claims process using AI-based document handling and workflow orchestration. Internal review is focused on pricing and the risk-based delivery model proposed by the team.",
      salesStage: 'Qualify',
      assessmentDecisionStatus: 'ReQualify',
      createdAt: '2025-01-20T09:00:00Z',
      updatedAt: '2025-03-05T14:00:00Z',
    },
    'Scotsman',
  ),
  mockOpp(
    '003',
    3,
    {
      title: 'Customer Portal Re-Platform',
      customerName: 'Fabrikam Industry Association',
      dealSize: 'M',
      company: 'CH',
      industry: MOCK_INDUSTRY_FSI_INSURANCE,
      submissionDeadline: '2025-04-30T12:00:00.000Z',
      opportunityOwner: {},
      briefDescription:
        'The association is looking to replace a legacy member portal with a modern self-service platform. The opportunity is in early qualification — key unknowns around procurement process and budget authority remain to be resolved before committing further.',
      salesStage: 'Qualify',
      createdAt: '2025-02-14T08:00:00Z',
      updatedAt: '2025-02-14T08:00:00Z',
    },
    'OpportunityMetadata',
  ),
  mockOpp(
    '004',
    4,
    {
      title: 'Smart Grid Analytics Platform',
      customerName: 'Litware Energy AG',
      cNumber: 'C-34312',
      dealSize: 'L',
      company: 'CH',
      industry: MOCK_INDUSTRY_ENERGY,
      submissionDeadline: '2025-04-01T12:00:00.000Z',
      opportunityOwner: mockCatalogUserFromEmail('mock.persone@zuehlke.com'),
      proposalManager: mockCatalogUserFromEmail('mock.personf@zuehlke.com'),
      briefDescription:
        "Proposal for a real-time grid analytics platform integrating sensor data, predictive load models, and operational dashboards for the client's distribution network. Submitted on time; awaiting evaluation results from the client's technical committee.",
      assessmentDecisionStatus: 'Go',
      salesStage: 'Develop',
      createdAt: '2025-01-08T08:00:00Z',
      updatedAt: '2025-04-01T09:00:00Z',
    },
    'OpportunityAssessment',
  ),
  mockOpp('005', 5, {
    title: 'HR Self-Service Transformation',
    customerName: 'Contoso Rail GmbH',
    cNumber: 'C-34198',
    dealSize: 'M',
    company: 'DE',
    industry: MOCK_INDUSTRY_TRANSPORT,
    submissionDeadline: '2025-02-15T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.persone@zuehlke.com'),
    briefDescription:
      "Zühlke won this engagement to deliver a self-service HR platform for the client's Swiss operations, consolidating leave management, onboarding, and payroll queries. Contract signed; project kick-off completed in March.",
    salesStage: 'Qualify',
    createdAt: '2024-11-10T08:00:00Z',
    updatedAt: '2025-02-28T12:00:00Z',
  }),
  mockOpp('006', 6, {
    title: 'Regulatory Reporting Engine',
    customerName: 'Wide World Bank AG',
    cNumber: 'C-34077',
    dealSize: 'L',
    company: 'CH',
    industry: MOCK_INDUSTRY_FSI_BANKING,
    submissionDeadline: '2025-01-28T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.personb@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.personb@zuehlke.com'),
    briefDescription:
      "A major regulatory reporting overhaul for the bank, covering regulator-mandated liquidity and capital reporting pipelines. Awarded after a competitive RFP; Zühlke's track record in FSI compliance engineering was the decisive differentiator.",
    assessmentDecisionStatus: 'NoGo',
    salesStage: 'Closed',
    createdAt: '2024-10-05T08:00:00Z',
    updatedAt: '2025-02-14T14:00:00Z',
  }),
  mockOpp('007', 7, {
    title: 'Patient Data Integration Hub',
    customerName: 'Adventure Regional Hospital',
    dealSize: 'M',
    company: 'CH',
    industry: MOCK_INDUSTRY_HEALTH,
    submissionDeadline: '2025-05-12T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.personc@zuehlke.com'),
    briefDescription:
      'The hospital is exploring a HL7 FHIR-based integration hub to connect disparate clinical systems across departments. The opportunity is nascent — budget is not yet formally approved and the RFP scope is still being defined with the client.',
    salesStage: 'Qualify',
    createdAt: '2025-03-01T08:00:00Z',
    updatedAt: '2025-03-01T08:00:00Z',
  }),
  mockOpp('008', 8, {
    title: 'Procurement Digitisation',
    customerName: 'Tailspin Retail Group',
    cNumber: 'C-34302',
    dealSize: 'M',
    company: 'CH',
    industry: MOCK_INDUSTRY_RETAIL,
    submissionDeadline: '2025-05-08T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.persond@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
    briefDescription:
      'The retailer is digitising supplier onboarding and purchase order flows across its retail and logistics divisions. Zühlke is building the proposal in response to a targeted RFQ; competitive pressure from two other regional integrators is expected.',
    salesStage: 'Qualify',
    createdAt: '2025-02-15T08:00:00Z',
    updatedAt: '2025-03-20T11:00:00Z',
  }),
  mockOpp('009', 9, {
    title: 'API Gateway Modernisation',
    customerName: 'Proseware Telecom AG',
    cNumber: 'C-34167',
    dealSize: 'M',
    company: 'CH',
    industry: MOCK_INDUSTRY_OTHER,
    submissionDeadline: '2025-03-10T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.persone@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.persone@zuehlke.com'),
    briefDescription:
      'Zühlke was not selected for this API platform modernisation. The contract was awarded to a competitor offering a lower day-rate and a pre-built accelerator. A retrospective has been scheduled to capture lessons for similar pipeline opportunities.',
    salesStage: 'Qualify',
    createdAt: '2024-12-14T08:00:00Z',
    updatedAt: '2025-03-28T10:00:00Z',
  }),
  mockOpp('010', 10, {
    title: 'Data Mesh Architecture',
    customerName: 'Alpine Federal Railways',
    cNumber: 'C-34511',
    dealSize: 'XL',
    company: 'AT',
    industry: MOCK_INDUSTRY_TRANSPORT,
    submissionDeadline: '2025-05-19T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.personf@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.personf@zuehlke.com'),
    briefDescription:
      "The national rail operator is evaluating a full data mesh transformation covering timetable, maintenance, and passenger experience data domains. This is a strategic pursuit for Zühlke's DACH transport vertical; SCOTSMAN scoring is in progress.",
    salesStage: 'Qualify',
    createdAt: '2025-03-10T08:00:00Z',
    updatedAt: '2025-03-10T08:00:00Z',
  }),
  mockOpp('011', 11, {
    title: 'Workforce Planning Tool',
    customerName: 'Contoso Postal Ltd',
    cNumber: 'C-34088',
    dealSize: 'S',
    company: 'CH',
    industry: MOCK_INDUSTRY_OTHER,
    submissionDeadline: '2025-01-05T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
    briefDescription:
      'After internal qualification, the bid team recommended a no-go due to low strategic fit, thin margin potential, and an incumbent vendor relationship that would be difficult to displace at this deal size. Decision endorsed by the Delivery Lead.',
    salesStage: 'Qualify',
    createdAt: '2024-11-20T08:00:00Z',
    updatedAt: '2025-01-05T10:00:00Z',
  }),
  mockOpp('012', 12, {
    title: 'Cloud Cost Optimisation',
    customerName: 'Northwind Holdings Insurance',
    cNumber: 'C-34388',
    dealSize: 'M',
    company: 'CH',
    industry: MOCK_INDUSTRY_FSI_INSURANCE,
    submissionDeadline: '2025-04-27T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.personb@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.personc@zuehlke.com'),
    briefDescription:
      'The insurer wants a rapid cloud spend assessment and optimisation roadmap across AWS and Azure. The proposal is in internal review; commercial terms and a risk-adjusted delivery option are still being finalised before sign-off.',
    salesStage: 'Qualify',
    createdAt: '2025-01-28T08:00:00Z',
    updatedAt: '2025-04-01T11:00:00Z',
  }),
  mockOpp('013', 13, {
    title: 'Intelligent Document Processing',
    customerName: 'Fabrikam General Insurance',
    cNumber: 'C-34502',
    dealSize: 'M',
    company: 'CH',
    industry: MOCK_INDUSTRY_FSI_INSURANCE,
    submissionDeadline: '2025-06-02T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.personc@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.persond@zuehlke.com'),
    briefDescription:
      'The insurer is investing in AI-driven processing of incoming claims and policy correspondence. Zühlke is positioning a solution combining OCR, LLM-based extraction, and human-in-the-loop review — differentiating on enterprise-grade governance.',
    salesStage: 'Qualify',
    createdAt: '2025-02-18T08:00:00Z',
    updatedAt: '2025-03-25T14:00:00Z',
  }),
  mockOpp('014', 14, {
    title: 'Digital Trade Finance Platform',
    customerName: 'Legacy Bank UK (merged entity)',
    cNumber: 'C-34444',
    dealSize: 'XL',
    company: 'UK',
    industry: MOCK_INDUSTRY_FSI_BANKING,
    submissionDeadline: '2025-03-18T12:00:00.000Z',
    opportunityOwner: mockCatalogUserFromEmail('mock.persone@zuehlke.com'),
    proposalManager: mockCatalogUserFromEmail('mock.persona@zuehlke.com'),
    briefDescription:
      'A complex cross-border engagement arising from a post-merger integration programme, targeting modernisation of legacy trade finance workflows. Submitted to a London-based evaluation panel; the timeline has slipped by three weeks on the client side.',
    salesStage: 'Qualify',
    createdAt: '2024-11-15T08:00:00Z',
    updatedAt: '2025-03-18T09:00:00Z',
  }),
];

mockQualificationBindOpportunities(MOCK_OPPORTUNITIES);

const MOCK_RESPONSES: MockDb = {
  'POST /api/opportunities/workspaces': {
    id: `pr-${Date.now()}`,
    workspaceId: mockWorkspaceId(999),
    title: '',
    customerName: '',
    cNumber: '',
    dealSize: 'M',
    salesStage: 'Qualify',
    step: 'UploadAndStart',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  'GET /api/knowledge-items/stats': MOCK_KNOWLEDGE_STATS_RESPONSE,
  'GET /api/opportunities/stats': MOCK_OPPORTUNITIES_STATS_RESPONSE,
};

interface PatternHandler {
  method: string;
  pattern: RegExp;
  handle: (url: string, body: unknown) => { status: number; body: unknown };
}

function mockReassignOpportunityOwner(
  id: string,
  userOId: string,
): { status: number; body: OpportunityResponseDto | { title: string; detail: string } | null } {
  if (!userOId.trim()) {
    return {
      status: 400,
      body: { title: 'Cannot reassign opportunity owner', detail: 'userOId is required.' },
    };
  }
  // Workaround until Momentum is rolled out: any directory user may be
  // the opportunity owner. Mandatory to revert before Strictly Confidential opportunities are
  // enabled — that owner receives a personal SharePoint grant.
  // const membership = mockUserInformation(userOId.trim());
  // if (membership && !membership.isMomentumUser) {
  //   return {
  //     status: 400,
  //     body: {
  //       title: 'Cannot reassign opportunity owner',
  //       detail: `'${userOId.trim()}' must be a MomentumUser to be an owner.`,
  //     },
  //   };
  // }
  return mockPatchOpportunity(
    id,
    {},
    {
      opportunityOwner: mockMomentumUserFromOId(userOId.trim()) ?? { oId: userOId.trim() },
    },
  );
}

function mockPatchOpportunity(
  id: string,
  patch: Record<string, unknown>,
  directFields?: Partial<OpportunityResponseDto>,
): { status: number; body: OpportunityResponseDto | { title: string; detail: string } | null } {
  if (patch['opportunityOwnerOId'] != null) {
    return {
      status: 400,
      body: {
        title: 'Cannot update opportunity',
        detail: 'Owner changes use POST /api/opportunities/{id}/owner.',
      },
    };
  }
  const index = MOCK_OPPORTUNITIES.findIndex((p) => p.id === id);
  if (index < 0) {
    return { status: 404, body: null };
  }
  const existing = MOCK_OPPORTUNITIES[index];
  const requestedStep =
    typeof patch['step'] === 'string' ? (patch['step'] as OpportunityApiStep) : undefined;
  const mergedStep = requestedStep
    ? furthestApiStep(existing.step as OpportunityApiStep | undefined, requestedStep)
    : existing.step;
  const { proposalManagerOId, bidTeamMembers: bidTeamMemberPatches, ...rest } = patch;
  const next: OpportunityResponseDto = {
    ...existing,
    ...(rest as Partial<OpportunityResponseDto>),
    ...directFields,
    step: mergedStep,
    updatedAt: new Date().toISOString(),
  };
  if (typeof proposalManagerOId === 'string') {
    next.proposalManager = proposalManagerOId.trim()
      ? mockMomentumUserFromOId(proposalManagerOId)
      : null;
  }
  if (Array.isArray(bidTeamMemberPatches)) {
    next.bidTeamMembers = bidTeamMemberPatches.map((member) => {
      const row = member as { userOId?: string | null; roleLabel?: string };
      return {
        user: mockMomentumUserFromOId(row.userOId),
        roleLabel: row.roleLabel ?? '',
      };
    });
  }
  const metadataChanged = mockPatchHasQualificationMetadataChange(existing, next);
  const updated = metadataChanged ? mockApplyQualificationMaterialChange(next, 'metadata') : next;
  MOCK_OPPORTUNITIES[index] = updated;
  return { status: 200, body: updated };
}

function mockLinkCrmOpportunity(
  id: string,
  body: unknown,
): {
  status: number;
  body: OpportunityResponseDto | { title: string; detail: string } | null;
} {
  const crmOpportunityIdRaw =
    typeof body === 'object' && body !== null && 'crmOpportunityId' in body
      ? String((body as { crmOpportunityId: string }).crmOpportunityId ?? '')
      : '';
  const requestedId = crmOpportunityIdRaw.trim();
  if (!requestedId) {
    return {
      status: 400,
      body: {
        title: 'Cannot link CRM opportunity',
        detail: 'CRM opportunity id cannot be empty.',
      },
    };
  }
  const index = MOCK_OPPORTUNITIES.findIndex((p) => p.id === id);
  if (index < 0) {
    return { status: 404, body: null };
  }
  const existing = MOCK_OPPORTUNITIES[index];
  if (existing.crmOpportunityId) {
    return {
      status: 409,
      body: {
        title: 'CRM opportunity already linked',
        detail: 'This opportunity is already linked to a CRM opportunity.',
      },
    };
  }
  const link = mockFindCrmOpportunityLink(requestedId);
  if (!link) {
    return {
      status: 400,
      body: {
        title: 'Cannot link CRM opportunity',
        detail: `CRM opportunity '${requestedId}' was not found.`,
      },
    };
  }
  const updated: OpportunityResponseDto = {
    ...existing,
    title: link.name,
    crmOpportunityId: link.opportunityId,
    crmProjectId: link.crmProjectId,
    cNumber: link.cNumber,
    crmOpportunityWebUrl: mockCrmOpportunityWebUrl(link.opportunityId),
    updatedAt: new Date().toISOString(),
  };
  MOCK_OPPORTUNITIES[index] = updated;
  return { status: 200, body: updated };
}

function mockSyncCrmLink(
  id: string,
  full: boolean,
): {
  status: number;
  body: OpportunityResponseDto | { title: string; detail: string } | null;
} {
  const index = MOCK_OPPORTUNITIES.findIndex((row) => row.id === id);
  if (index < 0) {
    return { status: 404, body: null };
  }
  const existing = MOCK_OPPORTUNITIES[index];
  if (existing.isCompleted) {
    return {
      status: 400,
      body: {
        title: 'Cannot sync CRM link',
        detail: 'Opportunity is already completed.',
      },
    };
  }
  const crmOpportunityId = existing.crmOpportunityId?.trim();
  if (!crmOpportunityId) {
    return {
      status: 400,
      body: {
        title: 'Cannot sync CRM link',
        detail: 'Opportunity is not linked to a CRM opportunity.',
      },
    };
  }
  const link = mockFindCrmOpportunityLink(crmOpportunityId);
  if (!link) {
    return {
      status: 400,
      body: {
        title: 'Cannot sync CRM link',
        detail: `CRM opportunity '${crmOpportunityId}' was not found.`,
      },
    };
  }
  const hasProject = Boolean(existing.crmProjectId?.trim() && existing.cNumber?.trim());
  if (!full && hasProject) {
    return { status: 200, body: existing };
  }
  const updated: OpportunityResponseDto = {
    ...existing,
    title: full ? link.name : existing.title,
    crmProjectId: full ? link.crmProjectId : (existing.crmProjectId ?? link.crmProjectId),
    cNumber: full ? link.cNumber : (existing.cNumber ?? link.cNumber),
    updatedAt: new Date().toISOString(),
  };
  MOCK_OPPORTUNITIES[index] = updated;
  return { status: 200, body: updated };
}

function mockCompleteOpportunity(id: string): {
  status: number;
  body: OpportunityResponseDto | { title: string; detail: string } | null;
} {
  const existing = MOCK_OPPORTUNITIES.find((row) => row.id === id);
  if (!existing) {
    return { status: 404, body: null };
  }
  if (existing.isCompleted) {
    return {
      status: 400,
      body: {
        title: 'Validation failed',
        detail: 'Opportunity is already completed.',
      },
    };
  }
  const decision = existing.assessmentDecisionStatus;
  if (decision !== 'Go' && decision !== 'NoGo') {
    return {
      status: 400,
      body: {
        title: 'Validation failed',
        detail: 'Opportunity can only be completed when the assessment decision is Go or NoGo.',
      },
    };
  }
  const now = new Date().toISOString();
  return mockPatchOpportunity(id, { isCompleted: true, completedAt: now });
}

const PATTERN_HANDLERS: PatternHandler[] = [
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/qualification-run$/,
    handle: (url) => {
      const match = /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/qualification-run$/.exec(
        url,
      );
      const id = match?.[1] ? decodeURIComponent(match[1]) : '';
      if (!MOCK_OPPORTUNITIES.some((row) => row.id === id)) {
        return { status: 404, body: null };
      }
      return {
        status: 202,
        body: { correlationId: `mock-qualification-run-${id}` },
      };
    },
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)$/,
    handle: (url) => {
      const id = url.split('/').pop();
      const opportunity = MOCK_OPPORTUNITIES.find((p) => p.id === id);
      return opportunity ? { status: 200, body: opportunity } : { status: 404, body: null };
    },
  },
  {
    method: 'DELETE',
    pattern: /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)$/,
    handle: (url, body) => {
      const id = decodeURIComponent(url.split('/').pop() ?? '');
      const index = MOCK_OPPORTUNITIES.findIndex((row) => row.id === id);
      if (index === -1) {
        return { status: 404, body: null };
      }
      const existing = MOCK_OPPORTUNITIES[index];
      const deleteReason =
        typeof body === 'object' && body !== null && 'deleteReason' in body
          ? (body as { deleteReason?: string | null }).deleteReason
          : null;
      if (existing.isCompleted && (!deleteReason || !String(deleteReason).trim())) {
        return {
          status: 400,
          body: {
            title: 'Cannot delete opportunity',
            detail: 'Delete reason is required for completed opportunities.',
          },
        };
      }
      MOCK_OPPORTUNITIES.splice(index, 1);
      return { status: 204, body: null };
    },
  },
  {
    method: 'PUT',
    pattern: /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/complete$/,
    handle: (url) => {
      const id = decodeURIComponent(url.split('/').at(-2) ?? '');
      return mockCompleteOpportunity(id);
    },
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/owner$/,
    handle: (url, body) => {
      const id = decodeURIComponent(url.split('/').at(-2) ?? '');
      const userOId =
        typeof body === 'object' && body !== null && 'userOId' in body
          ? String((body as { userOId: string }).userOId ?? '')
          : '';
      return mockReassignOpportunityOwner(id, userOId);
    },
  },
  {
    method: 'PUT',
    pattern: /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/links$/,
    handle: (url, body) => {
      const id = decodeURIComponent(url.split('/').at(-2) ?? '');
      const miroWebUrl =
        typeof body === 'object' && body !== null && 'miroWebUrl' in body
          ? (body as { miroWebUrl?: string | null }).miroWebUrl
          : undefined;
      return mockPatchOpportunity(id, {
        miroWebUrl: miroWebUrl ?? '',
      });
    },
  },
  {
    method: 'PUT',
    pattern: /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)$/,
    handle: (url, body) => {
      const id = url.split('/').pop() ?? '';
      const patch =
        typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
      const { miroWebUrl: _ignoredMiroWebUrl, ...withoutMiroWebUrl } = patch;
      return mockPatchOpportunity(id, withoutMiroWebUrl);
    },
  },
  {
    method: 'GET',
    pattern: /^\/api\/knowledge-items\/([^/]+)\/versions\/(\d+)$/,
    handle: (url) => {
      const match = /^\/api\/knowledge-items\/([^/]+)\/versions\/(\d+)$/.exec(url);
      if (!match) {
        return { status: 404, body: null };
      }
      const body = mockKnowledgeVersionDetailBody(match[1], Number.parseInt(match[2], 10));
      return body ? { status: 200, body } : { status: 404, body: null };
    },
  },
  {
    method: 'GET',
    pattern: /^\/api\/knowledge-items\/([^/]+)\/versions$/,
    handle: (url) => {
      const match = /^\/api\/knowledge-items\/([^/]+)\/versions$/.exec(url);
      if (!match) {
        return { status: 404, body: null };
      }
      return { status: 200, body: mockKnowledgeVersionListBody(match[1]) };
    },
  },
  {
    method: 'GET',
    pattern: /^\/api\/knowledge-items\/(.+)$/,
    handle: (url) => {
      const id = url.split('/').pop();
      const item = MOCK_KNOWLEDGE_ITEMS.find((i) => i.id === id);
      return item ? { status: 200, body: item } : { status: 404, body: null };
    },
  },
  {
    method: 'POST',
    pattern: /^\/api\/knowledge-items$/,
    handle: (_url, body) => {
      const partial = body as Partial<KnowledgeItemResponse>;
      const newItem: KnowledgeItemResponse = {
        ...partial,
        id: `ki-${Date.now()}`,
        owner: partial.owner ?? MOCK_MOMENTUM_USER,
        status: partial.status ?? 'Unverified',
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        needsReview: mockStubNeedReview(partial.reviewDate ?? undefined),
      } as KnowledgeItemResponse;
      MOCK_KNOWLEDGE_ITEMS.unshift(newItem);
      return { status: 201, body: newItem };
    },
  },
  {
    method: 'PUT',
    pattern: /^\/api\/knowledge-items\/(.+)$/,
    handle: (url, body) => {
      const id = url.split('/').pop();
      if (!id) return { status: 400, body: null };
      const index = MOCK_KNOWLEDGE_ITEMS.findIndex((i) => i.id === id);
      if (index === -1) return { status: 404, body: null };
      const payload = body as Partial<KnowledgeItemResponse> & { ownerOId?: string };
      const owner = payload.ownerOId
        ? (mockMomentumUserFromOId(payload.ownerOId) ?? MOCK_KNOWLEDGE_ITEMS[index].owner)
        : MOCK_KNOWLEDGE_ITEMS[index].owner;
      const { ownerOId: _ownerOId, ...rest } = payload;
      const merged = {
        ...MOCK_KNOWLEDGE_ITEMS[index],
        ...rest,
        owner,
        id: id,
        version: (MOCK_KNOWLEDGE_ITEMS[index].version ?? 1) + 1,
        updatedAt: new Date().toISOString(),
      };
      const updated: KnowledgeItemResponse = {
        ...merged,
        needsReview: mockStubNeedReview(merged.reviewDate ?? undefined),
      };
      MOCK_KNOWLEDGE_ITEMS[index] = updated;
      return { status: 200, body: updated };
    },
  },
  {
    method: 'DELETE',
    pattern: /^\/api\/knowledge-items\/(.+)$/,
    handle: (url) => {
      const id = url.split('/').pop();
      const index = MOCK_KNOWLEDGE_ITEMS.findIndex((i) => i.id === id);
      if (index !== -1) MOCK_KNOWLEDGE_ITEMS.splice(index, 1);
      return { status: 204, body: null };
    },
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/smart-discovery\/answer-models$/,
    handle: () => mockHandleSmartDiscoveryAnswerModelsGet(),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/workspaces\/[^/]+\/conversations$/,
    handle: () => mockHandleWorkspaceConversationsListGet(),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/workspaces\/[^/]+\/conversations$/,
    handle: (_url, body) => mockHandleWorkspaceConversationsCreatePost(body),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/workspaces\/[^/]+\/conversations\/[^/]+\/messages$/,
    handle: (url) => mockHandleWorkspaceConversationsMessagesGet(url),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/workspaces\/[^/]+\/conversations\/[^/]+$/,
    handle: (url) => mockHandleWorkspaceConversationsDetailGet(url),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/workspaces\/[^/]+\/conversations\/[^/]+\/chat$/,
    handle: (url, body) => mockHandleWorkspaceConversationsChatPost(url, body),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/[^/]+\/scotsman\/assessment$/,
    handle: (canonical) => mockHandleScotsmanAssessmentGet(canonical),
  },
  {
    method: 'PATCH',
    pattern: /^\/api\/opportunities\/[^/]+\/scotsman\/assessment\/dimensions\/[^/]+$/,
    handle: (canonical, body) => mockHandleScotsmanDimensionPatch(canonical, body),
  },
  {
    method: 'POST',
    pattern:
      /^\/api\/opportunities\/[^/]+\/scotsman\/assessment\/dimensions\/[^/]+\/acknowledge-ai-suggestion-change$/,
    handle: (canonical) => mockHandleScotsmanAcknowledgeAiSuggestionChange(canonical),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/scotsman\/assessment\/analyze$/,
    handle: (canonical, body) => mockHandleScotsmanAssessmentAnalyze(canonical, body),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/scotsman\/assessment\/finalize$/,
    handle: (canonical) => mockHandleScotsmanAssessmentFinalize(canonical),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment$/,
    handle: (canonical) => mockHandleOpportunityAssessmentGet(canonical),
  },
  {
    method: 'PATCH',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment$/,
    handle: (canonical, body) => mockHandleOpportunityAssessmentPatch(canonical, body),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment\/risks$/,
    handle: (canonical) => mockHandleOpportunityBidRisksGet(canonical),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment\/intelligence$/,
    handle: (canonical) => mockHandleOpportunityIntelligenceGet(canonical),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment\/intelligence\/analyze$/,
    handle: (canonical) => mockHandleOpportunityIntelligenceAnalyze(canonical),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment\/intelligence\/finalize$/,
    handle: (canonical) => mockHandleOpportunityIntelligenceFinalize(canonical),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment\/opportunity-value$/,
    handle: (canonical) => mockHandleOpportunityValueGet(canonical),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment\/opportunity-value\/analyze$/,
    handle: (canonical) => mockHandleOpportunityValueAnalyze(canonical),
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/assessment\/risks\/analyze$/,
    handle: (canonical) => mockHandleBidRiskAnalyze(canonical),
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/[^/]+\/qualification\/wizard-state$/,
    handle: (canonical) =>
      mockHandleQualificationWizardStateGet(canonical) ?? { status: 404, body: null },
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/qualification\/upload\/finalize$/,
    handle: (canonical) =>
      mockHandleQualificationUploadFinalize(canonical) ?? { status: 404, body: null },
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/smart-discovery\/intelligence\/finalize$/,
    handle: (canonical) =>
      mockHandleDiscoveryIntelligenceFinalize(canonical) ?? { status: 404, body: null },
  },
  {
    method: 'POST',
    pattern: /^\/api\/opportunities\/[^/]+\/smart-discovery\/intelligence\/analyze$/,
    handle: (canonical) =>
      mockHandleDiscoveryIntelligenceAnalyze(canonical) ?? { status: 404, body: null },
  },
  {
    method: 'GET',
    pattern: /^\/api\/opportunities\/[^/]+\/smart-discovery\/intelligence(\/|$)/,
    handle: (canonical) =>
      mockHandleDiscoveryIntelligenceGet(canonical) ?? { status: 404, body: null },
  },
];

/** Maps a request URL to its feature key so per-feature flags can be checked. */
function mockHttpResponse(
  feature: keyof MockApiFlags,
  req: HttpRequest<unknown>,
  canonical: string,
  status: number,
  body: unknown,
  delayMs = SIMULATED_DELAY_MS,
) {
  logMockApiExchange({
    feature,
    method: req.method,
    requestUrl: req.url,
    canonicalPath: canonical,
    requestBody: req.body,
    status,
    responseBody: body,
  });
  return of(new HttpResponse({ status, body })).pipe(delay(delayMs));
}

function mockAssetsHttpResponse(
  req: HttpRequest<unknown>,
  canonical: string,
  status: number,
  body: unknown,
  binary?: MockAssetsBinaryResponse,
) {
  if (binary) {
    return of(
      new HttpResponse({
        status,
        body: binary.blob,
        headers: new HttpHeaders({
          'Content-Type': binary.contentType,
          'Content-Disposition': `attachment; filename="${binary.fileName.replace(/"/g, '')}"`,
        }),
      }),
    ).pipe(delay(getAssetsMockTiming().responseDelayMs));
  }

  return mockHttpResponse(
    'assets',
    req,
    canonical,
    status,
    body,
    getAssetsMockTiming().responseDelayMs,
  );
}

function resolveFeature(url: string, base: string, flags: MockApiFlags): keyof MockApiFlags | null {
  const path = url.startsWith(base) ? url.slice(base.length) : url;
  if (path.includes('/smart-discovery')) return 'smartDiscovery';
  if (/\/opportunities\/workspaces\/[^/]+\/conversations(\/|$)/.test(path)) return 'smartDiscovery';
  if (/\/opportunities\/[^/]+\/scotsman(\/|$)/.test(path)) return 'scotsman';
  if (/\/opportunities\/[^/]+\/assessment(\/|$)/.test(path)) {
    return 'opportunityAssessment';
  }
  if (path.startsWith('/opportunities')) return 'proposals';
  if (path.startsWith('/accounts')) return 'accounts';
  if (path.startsWith('/knowledge-items')) return 'knowledge';
  if (path.startsWith('/industries')) {
    if (flags.knowledge) return 'knowledge';
    if (flags.accounts) return 'accounts';
    return null;
  }
  if (path.startsWith('/companies')) {
    if (flags.proposals) return 'proposals';
    if (flags.accounts) return 'accounts';
    return null;
  }
  if (path.startsWith('/assets')) return 'assets';
  if (path.startsWith('/projects') && flags.assets) return 'assets';
  return null;
}

export const mockApiInterceptor: HttpInterceptorFn = (req, next) => {
  const env = inject(APP_ENV);
  const base = env.apiBaseUrl;

  if (!req.url.startsWith(base)) {
    return next(req);
  }

  const flags = env.enableMockApi;
  const urlWithoutQuery = req.url.split('?')[0];
  const canonicalEarly = urlWithoutQuery.replace(base, '/api');

  if (
    req.method === 'GET' &&
    canonicalEarly === '/api/users' &&
    (flags.proposals || flags.knowledge || flags.opportunityAssessment || flags.accounts)
  ) {
    const params = readUrlSearchParams(req.urlWithParams);
    const search = req.params.get('search') ?? params.get('search') ?? '';
    const limit = Number(req.params.get('limit') ?? params.get('limit') ?? '20');
    return mockHttpResponse(
      flags.proposals ? 'proposals' : 'knowledge',
      req,
      canonicalEarly,
      200,
      searchMockDirectoryUsers(search, Number.isFinite(limit) ? limit : 20),
    );
  }

  const userInformationMatch = canonicalEarly.match(/^\/api\/users\/([^/]+)\/information$/);
  if (
    req.method === 'GET' &&
    userInformationMatch &&
    (flags.proposals || flags.knowledge || flags.opportunityAssessment || flags.accounts)
  ) {
    const oId = decodeURIComponent(userInformationMatch[1]);
    const information = mockUserInformation(oId);
    if (!information) {
      return mockHttpResponse(
        flags.proposals ? 'proposals' : 'knowledge',
        req,
        canonicalEarly,
        404,
        null,
      );
    }

    return mockHttpResponse(
      flags.proposals ? 'proposals' : 'knowledge',
      req,
      canonicalEarly,
      200,
      information,
    );
  }

  const feature = resolveFeature(urlWithoutQuery, base, flags);

  if (feature === null || !flags[feature]) {
    return next(req);
  }

  // Normalise to the canonical /api prefix used by all MOCK_RESPONSES keys and PATTERN_HANDLERS
  const canonical = urlWithoutQuery.replace(base, '/api');

  if (feature === 'assets') {
    const result = mockHandleAssetsRoute(req.method, canonical, req.body, req.url);
    if (result) {
      return mockAssetsHttpResponse(req, canonical, result.status, result.body, result.binary);
    }
    return mockAssetsHttpResponse(req, canonical, 404, null);
  }

  if (req.method === 'GET' && feature === 'proposals') {
    const exportMatch = /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/export$/.exec(
      canonical,
    );
    if (exportMatch) {
      const id = decodeURIComponent(exportMatch[1]);
      const opportunity = MOCK_OPPORTUNITIES.find((row) => row.id === id);
      if (!opportunity) {
        return mockHttpResponse(feature, req, canonical, 404, null);
      }
      const blob = new Blob(['# Mock export\n'], { type: 'text/markdown' });
      logMockApiExchange({
        feature,
        method: req.method,
        requestUrl: req.url,
        canonicalPath: canonical,
        requestBody: req.body,
        status: 200,
        responseBody: blob,
      });
      return of(
        new HttpResponse({
          status: 200,
          body: blob,
          headers: new HttpHeaders({
            'Content-Type': 'text/markdown; charset=utf-8',
            'Content-Disposition': 'attachment; filename="opportunity-mock.md"',
          }),
        }),
      ).pipe(delay(SIMULATED_DELAY_MS));
    }
  }

  if (feature === 'accounts') {
    const companiesResult = mockHandleCompaniesRoute(req.method, canonical, req.urlWithParams);
    if (companiesResult) {
      return mockHttpResponse(
        feature,
        req,
        canonical,
        companiesResult.status,
        companiesResult.body,
      );
    }
    const result = mockHandleAccountsRoute(req.method, canonical, req.body, req.urlWithParams);
    if (result) {
      return mockHttpResponse(feature, req, canonical, result.status, result.body);
    }
    return mockHttpResponse(feature, req, canonical, 404, null);
  }

  if (req.method === 'GET' && canonical === '/api/knowledge-items') {
    const body = mockKnowledgeListBody(req.url);
    return mockHttpResponse(feature, req, canonical, 200, body);
  }

  if (req.method === 'GET' && canonical === '/api/opportunities') {
    const body = mockOpportunityListBody(req.url);
    return mockHttpResponse(feature, req, canonical, 200, body);
  }

  if (req.method === 'GET' && canonical === '/api/opportunities/directory') {
    const params = readUrlSearchParams(req.urlWithParams);
    const crmAccountId = params.get('crmAccountId') ?? '';
    return mockHttpResponse(
      feature,
      req,
      canonical,
      200,
      mockListCrmOpportunitiesByAccount(crmAccountId),
    );
  }

  if (req.method === 'POST' && /\/crm-link$/.test(canonical)) {
    const match = /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/crm-link$/.exec(canonical);
    const id = match?.[1] ? decodeURIComponent(match[1]) : '';
    const result = mockLinkCrmOpportunity(id, req.body);
    return mockHttpResponse(feature, req, canonical, result.status, result.body);
  }

  if (req.method === 'POST' && /\/crm-sync$/.test(canonical)) {
    const match = /^\/api\/opportunities\/(?!workspaces(?:\/|$))([^/]+)\/crm-sync$/.exec(canonical);
    const id = match?.[1] ? decodeURIComponent(match[1]) : '';
    const params = readUrlSearchParams(req.urlWithParams);
    const full = params.get('full') === 'true';
    const result = mockSyncCrmLink(id, full);
    return mockHttpResponse(feature, req, canonical, result.status, result.body);
  }

  const industriesResult = mockHandleIndustriesRoute(req.method, canonical, req.url);
  if (industriesResult) {
    return mockHttpResponse(
      feature,
      req,
      canonical,
      industriesResult.status,
      industriesResult.body,
    );
  }

  const companiesResult = mockHandleCompaniesRoute(req.method, canonical, req.urlWithParams);
  if (companiesResult) {
    return mockHttpResponse(feature, req, canonical, companiesResult.status, companiesResult.body);
  }

  const workspaceFilesResult = mockHandleWorkspaceFilesRoute(
    req.method,
    canonical,
    MOCK_OPPORTUNITIES,
  );
  if (workspaceFilesResult) {
    return mockHttpResponse(
      feature,
      req,
      canonical,
      workspaceFilesResult.status,
      workspaceFilesResult.body,
    );
  }

  // Exact-match lookup (handles simple GET endpoints)
  const key = `${req.method} ${canonical}`;
  const body = MOCK_RESPONSES[key];

  if (body !== undefined) {
    return mockHttpResponse(feature, req, canonical, 200, body);
  }

  // Pattern-based lookup for parameterised routes
  for (const handler of PATTERN_HANDLERS) {
    if (handler.method === req.method && handler.pattern.test(canonical)) {
      const result = handler.handle(canonical, req.body);
      const delayMs =
        feature === 'scotsman' &&
        typeof result === 'object' &&
        result !== null &&
        'delayMs' in result &&
        typeof (result as { delayMs?: unknown }).delayMs === 'number'
          ? (result as { delayMs: number }).delayMs
          : feature === 'smartDiscovery' &&
              typeof result === 'object' &&
              result !== null &&
              'delayMs' in result &&
              typeof (result as { delayMs?: unknown }).delayMs === 'number'
            ? (result as { delayMs: number }).delayMs
            : SIMULATED_DELAY_MS;
      return mockHttpResponse(feature, req, canonical, result.status, result.body, delayMs);
    }
  }

  return mockHttpResponse(feature, req, canonical, 404, null);
};
